import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { useDocumentHead } from '@/hooks/use-document-head';
import { CountUp } from '@/components/ui/count-up';

const COMMERCIAL_SERVICES = [
  { title: 'OFFICE CLEANING', icon: '🏢', desc: 'Daily, weekly, or custom schedules. Desks, breakrooms, restrooms, lobbies — razor-sharp every morning.' },
  { title: 'FLOOR CARE', icon: '✨', desc: 'VCT stripping, waxing, carpet extraction, hard surface polishing. Your floors reflect your standards.' },
  { title: 'RESTROOM SANITATION', icon: '🧴', desc: 'Hospital-grade disinfection. Stock replenishment. Odor control. The room nobody talks about — until it\'s a problem.' },
  { title: 'WINDOW & GLASS', icon: '🪟', desc: 'Interior glass, exterior ground-level, partitions. Streak-free, every time.' },
  { title: 'POST-EVENT', icon: '🎉', desc: 'Conference rooms, event spaces, lobbies after high-traffic days. We reset your space to showroom condition.' },
  { title: 'EXECUTIVE SUITES', icon: '👔', desc: 'White-glove detail for C-suite offices, boardrooms, and client-facing areas. The spaces where impressions are made.' },
];

export default function CommercialPage() {
  useDocumentHead(
    'Commercial & Office Cleaning in Orlando | SOTSVC',
    'Commercial & office cleaning for Orlando & Central Florida businesses from Sonz of Thunder Services (SOTSVC). Free quotes.',
  );
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[500px] clip-diag-bottom overflow-hidden">
        <img src="/images/heroes/hero-6-skyline-office.webp" alt="" className="absolute inset-0 w-full h-full object-cover ken-burns" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,15,30,.9) 0%, rgba(15,15,30,.55) 50%, rgba(0,180,216,.3) 100%)' }} />
        <div className="absolute inset-0 opacity-20 grid-pattern" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full fade-in">
            <div className="flex items-center gap-4 mb-6">
              <span className="h-[3px] w-16 bg-thunder" />
              <span className="text-thunder font-display tracking-[0.4em] text-xs">// FOR FACILITY MANAGERS</span>
            </div>
            <h1 className="font-display font-bold text-white text-shadow-bold leading-[0.88] mb-6">
              <span className="block text-4xl sm:text-6xl md:text-8xl">COMMERCIAL</span>
              <span className="block text-5xl sm:text-7xl md:text-9xl text-thunder" style={{ textShadow: '0 0 60px rgba(255,214,10,.5)' }}>CLEANING</span>
            </h1>
            <p className="text-white/80 text-lg sm:text-xl max-w-xl font-light">
              After-hours crews. Owner-supervised. Your building passes inspection — every single time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
                className="pulse-btn bg-electric hover:bg-electric-bright text-charcoal-deep font-display font-bold px-10 py-5 text-sm tracking-[0.3em] transition glow-electric w-full sm:w-auto text-center"
              >
                GET COMMERCIAL QUOTE →
              </button>
              <a href="tel:4074616039" className="border-2 border-thunder text-thunder hover:bg-thunder hover:text-charcoal-deep font-display font-bold px-10 py-5 text-sm tracking-[0.3em] transition w-full sm:w-auto text-center">
                CALL DIRECT
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Commercial */}
      <section className="relative py-28 bg-charcoal-deep overflow-hidden noise">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <ScrollReveal direction="left">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-[2px] w-10 bg-electric" />
              <span className="text-electric font-display tracking-[0.4em] text-xs">// WHY SOTSVC</span>
            </div>
            <h2 className="font-display font-bold text-white text-5xl sm:text-6xl leading-[0.9] mb-8">
              YOUR BUILDING IS YOUR <span className="text-thunder">BRAND.</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-6">
              Clients judge your business the second they walk through the door. Employees perform better in clean environments. Inspectors notice everything. We make sure your space tells the right story.
            </p>
            <ul className="space-y-3 mb-8">
              {['Coverall-trained methodology', 'Background-checked, uniformed crews', 'After-hours availability', 'Consistent same-crew assignments', 'Monthly quality inspections', 'Custom scope of work'].map((item) => (
                <li key={item} className="flex items-center gap-4 text-white/80 font-display tracking-wider text-sm">
                  <span className="text-electric text-xl">&#x25B8;</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </ScrollReveal>

          <ScrollReveal direction="right" className="relative">
            <div className="absolute -inset-4 bg-electric/10 blur-3xl" />
            <div className="relative overflow-hidden border-2 border-electric/40">
              <img src="/images/heroes/hero-7-twilight-lobby.webp" alt="Commercial lobby" className="w-full aspect-[4/3] object-cover" />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Services Grid */}
      <section className="relative py-28 bg-charcoal overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <ScrollReveal className="text-center mb-16">
            <div className="text-electric font-display tracking-[0.4em] text-xs mb-3">// COMMERCIAL SERVICES</div>
            <h2 className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
              THE <span className="text-thunder">ARSENAL</span>
            </h2>
          </ScrollReveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {COMMERCIAL_SERVICES.map((s, i) => (
              <ScrollReveal key={s.title} delay={i * 80}>
                <div className="bg-charcoal-deep border-l-4 border-electric hover:border-thunder p-8 h-full hover:-translate-y-2 transition-all duration-300">
                  <div className="text-4xl mb-4">{s.icon}</div>
                  <h3 className="font-display font-bold text-white text-xl mb-3 tracking-wider">{s.title}</h3>
                  <p className="text-white/60 leading-relaxed">{s.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative py-20 bg-charcoal-deep border-y-2 border-electric/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { v: 200, s: '+', label: 'Commercial Clients' },
              { v: 500000, s: '', label: 'Sq Ft Cleaned Monthly', display: '500K+' },
              { v: 100, s: '%', label: 'Inspection Pass Rate' },
              { v: 1, s: 'hr', label: 'Response Time' },
            ].map((stat) => (
              <ScrollReveal key={stat.label}>
                <div className="font-display font-bold text-electric text-5xl sm:text-6xl leading-none mb-3" style={{ textShadow: '0 0 40px rgba(0,180,216,.5)' }}>
                  {stat.display || <CountUp end={stat.v} suffix={stat.s} />}
                </div>
                <div className="h-[2px] w-12 bg-thunder mx-auto mb-3" />
                <div className="font-display text-white tracking-[0.2em] text-[10px] uppercase">{stat.label}</div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 overflow-hidden clip-diag-both">
        <img src="/images/heroes/hero-6-skyline-office.webp" alt="" className="absolute inset-0 w-full h-full object-cover ken-burns" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(15,15,30,.8) 0%, rgba(15,15,30,.6) 50%, rgba(15,15,30,.9) 100%)' }} />
        <ScrollReveal className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display font-bold text-white text-5xl sm:text-6xl md:text-7xl leading-[0.88] mb-8">
            LET'S TALK <span className="text-thunder">COMMERCIAL.</span>
          </h2>
          <p className="text-white/80 text-lg max-w-xl mx-auto mb-10">
            Free walkthrough. Custom scope of work. Transparent pricing per square foot.
          </p>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
            className="pulse-btn bg-electric hover:bg-electric-bright text-charcoal-deep font-display font-bold px-12 py-5 text-sm tracking-[0.3em] transition glow-electric"
          >
            SCHEDULE WALKTHROUGH →
          </button>
        </ScrollReveal>
      </section>
    </div>
  );
}
